import { useState, useEffect, useCallback } from 'react';
import ClassSearch from './ClassSearch';
import ClassList from './ClassList';
import ActionButtons from './ActionButtons';
import { loadUserDetails } from '../../../functions/Auth';
import {
  getClassList,
  syllabiExist,
  getTerms,
  getUserDetail,
} from '../../../functions/Api';
import RemoveDialog from './RemoveDialog';
import Spinner from '../../Spinner';
import { Button } from '@asu/unity-react-core';

function Classes({ isAdmin, viewAs }) {
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [removing, setRemoving] = useState(false);
  const [removalResults, setRemovalResults] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [classesLoading, setClassesLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [terms, setTerms] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState('');
  const user = loadUserDetails();

  const fetchClasses = useCallback(async (asurite = null, termToUse = null) => {
    if (!termToUse) return;

    setClassesLoading(true);
    try {
      if (asurite && termToUse) {
        const res = await getClassList(asurite, termToUse);

        let termClasses;
        if (res.length > 1) {
          termClasses = res;
        } else {
          termClasses = res[termToUse];
        }

        if (termClasses) {
          if (termClasses.length > 0) {
            termClasses = await syllabiExist(termClasses);
          }
          setClasses(termClasses);
        } else {
          console.warn('termClasses not defined for term:', termToUse);
          setClasses([]);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setClassesLoading(false);
    }
  }, []);

  // Initialize terms and fetch classes for non-admin users
  useEffect(() => {
    async function init() {
      setClassesLoading(true);
      try {
        const fetchedTerms = await getTerms();
        setTerms(fetchedTerms);
        setSelectedTerm(fetchedTerms.workingTerm);

        // Fetch classes immediately for non-admin users
        if (user && !isAdmin) {
          await fetchClasses(viewAs, fetchedTerms.workingTerm);
        }
      } catch (err) {
        console.log(err);
      }
    }
    init();
  }, []);

  // Fetch classes when selectedTerm changes (user selects different term)
  useEffect(() => {
    if (selectedTerm && user && !isAdmin) {
      fetchClasses(viewAs ?? user.asuriteid, selectedTerm);
    }
  }, [selectedTerm, fetchClasses, viewAs]);

  // Handle reload trigger
  useEffect(() => {
    if (reload && selectedTerm) {
      fetchClasses(viewAs ?? user.asuriteid, selectedTerm);
      setReload(false);
    }
  }, [reload, selectedTerm, fetchClasses, viewAs]);

  // Handle viewAs changes
  useEffect(() => {
    if ((viewAs || !isAdmin) && selectedTerm) {
      fetchClasses(viewAs ?? user.asuriteid, selectedTerm);
    }
  }, [viewAs, selectedTerm, fetchClasses]);

  // Update classes after removal
  useEffect(() => {
    if (removalResults.length > 0 && classes.length > 0) {
      async function updateAfterRemoval() {
        setClassesLoading(true);
        try {
          const updatedClasses = await syllabiExist(classes);
          setClasses(updatedClasses);
        } catch (err) {
          console.log(err);
        } finally {
          setClassesLoading(false);
        }
      }
      updateAfterRemoval();
    }
  }, [removalResults]);

  return (
    <>
      {
        <div className="classes">
          {isAdmin && !viewAs ? <h2>Find Classes</h2> : <h2>My Classes</h2>}
          {removalResults.length > 0 && (
            <>
              {removalResults.map((removalResult, index) => (
                <div
                  key={index}
                  className="alert alert-success alert-dismissable fade show"
                  role="alert"
                >
                  <div className="alert-icon">
                    <svg
                      title="Success"
                      className="svg-inline--fa fa-check-circle fa-w-16"
                      aria-labelledby="svg-inline--fa-title-V4z4dpVdXhk6"
                      data-prefix="fa"
                      data-icon="check-circle"
                      role="img"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      data-fa-i2svg=""
                    >
                      <title id="svg-inline--fa-title-V4z4dpVdXhk6">
                        Success
                      </title>
                      <path
                        fill="currentColor"
                        d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"
                      ></path>
                    </svg>
                  </div>
                  <div className="alert-content">
                    Syllabus for class {removalResult.classNumber} has been
                    deleted.
                  </div>
                  <div className="alert-close">
                    <button
                      aria-label="Close"
                      className="btn btn-circle btn-circle-alt-black close"
                      // data-dismiss="alert"
                      // data-ga="close cross"
                      // data-ga-action="close"
                      // onClick = {()=> setUploadResults(uploadResults.splice(index,1))}
                      onClick={() => setRemovalResults('')}
                      data-dismiss="alert"
                      data-ga-event="modal"
                      data-ga-name="onclick"
                      // data-ga-region="main content"
                      // data-ga-type="click"
                      type="button"
                    >
                      {/* <i class="fas fa-times" /> */}
                      <svg
                        className="svg-inline--fa fa-times fa-w-11"
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="fas"
                        data-icon="times"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 352 512"
                        data-fa-i2svg=""
                      >
                        <path
                          fill="currentColor"
                          d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
                        ></path>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          {isAdmin && !viewAs ? (
            <ClassSearch
              classes={classes}
              setClasses={setClasses}
              loading={classesLoading}
              setLoading={setClassesLoading}
              reload={reload}
              setSelectedClasses={setSelectedClasses}
            />
          ) : (
            <form
              className="row uds-form align-items-center"
              autoComplete="off"
            >
              <div className="form-group col-md-4 col-sm-12 mb-3">
                <label>Term</label>
                <select
                  className="form-select"
                  style={{ height: '42px' }}
                  id="term"
                  name="strm"
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                >
                  {terms &&
                    terms.termsList.map((term) => (
                      <option key={term.strm} value={term.strm}>
                        {term.description}
                      </option>
                    ))}
                </select>
              </div>
            </form>
          )}

          {(classesLoading || removing) && <Spinner />}

          {!(classesLoading || removing) && classes.length > 0 && (
            <>
              <ClassList
                classes={classes}
                setClasses={setClasses}
                selectedClasses={selectedClasses}
                setSelectedClasses={setSelectedClasses}
              />

              <ActionButtons
                selectedClasses={selectedClasses}
                setSelectedClasses={setSelectedClasses}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                removalResults={removalResults}
                setRemovalResults={setRemovalResults}
                removing={removing}
                setRemoving={setRemoving}
                reload={reload}
                setReload={setReload}
                viewAs={viewAs}
              />

              {/** Dialog box for displaying removal */}
              <RemoveDialog
                open={showDialog}
                setOpen={setShowDialog}
                removalResults={removalResults}
                setRemovalResults={setRemovalResults}
                removing={removing}
                setRemoving={setRemoving}
              />
            </>
          )}
        </div>
      }
    </>
  );
}

export default Classes;
